import React, { useRef, useEffect, useState } from "react"
import { Chart, ArcElement, Tooltip } from "chart.js"
import type { GroupRow, SeriesRow } from "~/api/snapshot_summaries"
import { formatCurrency } from "../utils"

Chart.register(ArcElement, Tooltip)

const COLORS = [
  "#143764",
  "#338cff",
  "#61bffd",
  "#4c9c8b",
  "#7cb9a5",
  "#c8873f",
  "#8a6fb0",
  "#c47b9a",
  "#9aa0a8",
]

type Dimension = "vintage" | "asset_class" | "age"

interface SegmentDonutProps {
  byVintage: GroupRow[]
  byAssetClass: GroupRow[]
  series: SeriesRow[]
}

function buildAgeBuckets(series: SeriesRow[]): GroupRow[] {
  const b: Record<string, number> = {
    "<2y": 0,
    "2–4y": 0,
    "4–6y": 0,
    ">6y": 0,
  }
  for (const s of series) {
    const age = Number(s.performance?.age ?? 0)
    const mv = Number(s.performance?.mv ?? 0)
    if (age < 2) b["<2y"] += mv
    else if (age < 4) b["2–4y"] += mv
    else if (age < 6) b["4–6y"] += mv
    else b[">6y"] += mv
  }
  return Object.entries(b)
    .filter(([, mv]) => mv > 0)
    .map(
      ([label, mv]) =>
        ({
          label,
          series_count: 0,
          commitment: 0,
          performance: { mv } as GroupRow["performance"],
          quarter_activity: {} as GroupRow["quarter_activity"],
          ytd_performance: {} as GroupRow["ytd_performance"],
        }) as GroupRow,
    )
}

function DonutChart({
  items,
  totalMv,
}: {
  items: GroupRow[]
  totalMv: number
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const chartRef = useRef<Chart<"doughnut"> | null>(null)
  const [hidden, setHidden] = useState<Set<number>>(new Set())

  useEffect(() => {
    setHidden(new Set())
  }, [items])

  useEffect(() => {
    if (!canvasRef.current || items.length === 0) return

    if (chartRef.current) {
      chartRef.current.destroy()
    }

    const ctx = canvasRef.current.getContext("2d")
    if (!ctx) return

    chartRef.current = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: items.map((r) => r.label),
        datasets: [
          {
            data: items.map((r) => Number(r.performance.mv ?? 0)),
            backgroundColor: items.map((_, i) => COLORS[i % COLORS.length]),
            borderColor: "#fff",
            borderWidth: 2,
          },
        ],
      },
      options: {
        cutout: "58%",
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const val = ctx.raw as number
                const pct =
                  totalMv > 0 ? ((val / totalMv) * 100).toFixed(1) : "0.0"
                return `${ctx.label}: ${formatCurrency(val)} (${pct}%)`
              },
            },
          },
        },
      },
    })

    const onResize = () => chartRef.current?.resize()
    window.addEventListener("resize", onResize)

    return () => {
      window.removeEventListener("resize", onResize)
      chartRef.current?.destroy()
      chartRef.current = null
    }
    // items is stable within a mounted DonutChart instance (key={dimension} remounts on toggle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, totalMv])

  function toggleItem(index: number) {
    const chart = chartRef.current
    if (!chart) return
    chart.toggleDataVisibility(index)
    chart.update()
    setHidden((prev) => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }

  return (
    <div>
      <div className="b-ps-chart-wrap">
        <canvas ref={canvasRef} />
      </div>
      <div className="b-ps-legend">
        {items.map((item, i) => {
          const mv = Number(item.performance.mv ?? 0)
          const pct = totalMv > 0 ? ((mv / totalMv) * 100).toFixed(1) : "0.0"
          const isHidden = hidden.has(i)
          return (
            <div
              key={item.label}
              className="b-ps-legend-item"
              style={{ opacity: isHidden ? 0.4 : 1 }}
              onClick={() => toggleItem(i)}
            >
              <div
                className="b-ps-legend-swatch"
                style={{ background: COLORS[i % COLORS.length] }}
              />
              <span>
                {item.label} ({pct}%)
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function SegmentDonut({
  byVintage,
  byAssetClass,
  series,
}: SegmentDonutProps) {
  const [dimension, setDimension] = useState<Dimension>("vintage")

  const ageBuckets = buildAgeBuckets(series)

  const activeItems =
    dimension === "vintage"
      ? byVintage
      : dimension === "asset_class"
      ? byAssetClass
      : ageBuckets

  const filtered = activeItems.filter((r) => Number(r.performance.mv ?? 0) > 0)
  const totalMv = filtered.reduce(
    (s, r) => s + Number(r.performance.mv ?? 0),
    0,
  )

  return (
    <div>
      <div className="b-ps-seg-toggle">
        {(["vintage", "asset_class", "age"] as Dimension[]).map((d) => (
          <button
            key={d}
            className={`b-ps-seg-btn${dimension === d ? " active" : ""}`}
            onClick={() => setDimension(d)}
          >
            {d === "vintage"
              ? "Vintage"
              : d === "asset_class"
              ? "Asset Class"
              : "Age"}
          </button>
        ))}
      </div>
      <div style={{ marginTop: 12 }}>
        <DonutChart key={dimension} items={filtered} totalMv={totalMv} />
      </div>
    </div>
  )
}
